import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { PostVoicemail, Voicemail, VoicemailRef } from '../models/generated/ziroModels';
import { ZiroModalService } from '../services/ziro-modal.service';
import { catchError } from 'rxjs/operators';
import { AuditHeaderService } from '../services/audit-header.service';
import { ZiroIcons } from '../models/ziro-icons.enum';
import { ButtonStyles } from '../../button/button.component';
import { Nvp } from '../models/nvp';
import { HttpOptions } from './abstract/model.resource.abstract';

@Injectable()
export class VoicemailResource {
  private http = inject(HttpClient);
  protected ziroModalService = inject(ZiroModalService);
  private auditHeaderService = inject(AuditHeaderService);

  get(unityServerId: number, id: string): Observable<Voicemail> {
    return this.http
      .get<Voicemail>(`/services/cisco/macs/unity-servers/${unityServerId}/voicemails/${id}`)
      .pipe(catchError(this._handleVoicemailError));
  }

  put(unityServerId: number, id: string, voicemail: Voicemail, auditTags?: Nvp[]): Observable<VoicemailRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http
      .put<VoicemailRef>(`/services/cisco/macs/unity-servers/${unityServerId}/voicemails/${id}`, voicemail, httpOptions)
      .pipe(catchError(this._handleVoicemailError));
  }

  delete(unityServerId: number, id: string, auditTags?: Nvp[]): Observable<void> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http
      .delete<void>(`/services/cisco/macs/unity-servers/${unityServerId}/voicemails/${id}`, httpOptions)
      .pipe(catchError(this._handleVoicemailError));
  }

  post(unityServerId: number, voicemail: PostVoicemail, auditTags?: Nvp[]): Observable<VoicemailRef> {
    const httpOptions = this._buildHttpOptions(auditTags);
    return this.http
      .post<VoicemailRef>(
        `/services/cisco/macs/unity-servers/${unityServerId}/voicemails`,
        {
          ...voicemail,
        },
        httpOptions,
      )
      .pipe(catchError(this._handleVoicemailError));
  }

  private _handleVoicemailError: (error: any) => Observable<never> = (error: any) => {
    const errorDescription = error ? error.error?.description : '';
    if (error.status === 422 && error.error.reasonCode === 'FORBIDDEN_DN_MODIFICATION') {
      const options = {
        modalViewProperties: {
          title: 'tkey;forbidden.user.title',
          promptBody: errorDescription,
          icon: ZiroIcons.FORBIDDEN,
          iconClass: 'text-danger',
          displayCloseButton: true,
          buttons: [
            {
              label: 'tkey;dialogs.button.ok',
              buttonClass: ButtonStyles.OUTLINE_INFO,
              dataAutomation: 'custom-error-modal-ok',
            },
          ],
        },
      };
      this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
    }
    return throwError(() => error);
  };

  private _buildHttpOptions(auditTags: Nvp[]): HttpOptions | object {
    return auditTags ? { headers: this.auditHeaderService.buildHeader(auditTags) } : {};
  }
}
